import type { MiniAppReportRequest } from '@exam-prep/contracts';
import { t } from '@exam-prep/i18n';
import { Button } from '@exam-prep/ui';
import { useState } from 'react';

type Reason = MiniAppReportRequest['reason'];

/** Type-only so the player bundle stays free of zod; the record makes a new reason a type error. */
const REASON_ORDER: Record<Reason, number> = {
  wrong_answer: 0,
  ambiguous: 1,
  typo: 2,
  out_of_syllabus: 3,
  other: 4,
};
const REASONS = (Object.keys(REASON_ORDER) as Reason[]).sort(
  (a, b) => REASON_ORDER[a] - REASON_ORDER[b],
);

/** What the player passes down for reporting (from `useReportQuestion`). */
export interface ReportControls {
  sent: ReadonlySet<string>;
  busy: boolean;
  failed: boolean;
  send: (itemId: string, reason: Reason, note: string) => Promise<boolean>;
}

/** "Report a problem" under a question: pick a reason, optionally add a note, send (ADR 0044). */
export function ReportQuestion({ itemId, report }: { itemId: string; report: ReportControls }) {
  const { sent, busy, failed, send } = report;
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState<Reason | null>(null);
  const [note, setNote] = useState('');
  if (sent.has(itemId))
    return <p className="text-xs text-muted-foreground">{t('report.thanks')}</p>;
  if (!open)
    return (
      <button
        type="button"
        className="text-xs text-muted-foreground underline"
        onClick={() => setOpen(true)}
      >
        {t('miniapp.player.report')}
      </button>
    );
  const close = () => {
    setOpen(false);
    setReason(null);
    setNote('');
  };
  return (
    <div className="space-y-2 rounded-xl border bg-card p-3 text-sm">
      <p className="font-medium">{t('report.prompt')}</p>
      <div className="flex flex-wrap gap-2">
        {REASONS.map((r) => (
          <Button
            key={r}
            size="sm"
            variant={reason === r ? 'secondary' : 'outline'}
            aria-pressed={reason === r}
            onClick={() => setReason(r)}
          >
            {t(`report.reason.${r}`)}
          </Button>
        ))}
      </div>
      <textarea
        className="w-full rounded-md border border-input bg-background p-2 text-sm"
        rows={2}
        maxLength={500}
        placeholder={t('miniapp.player.report_note')}
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />
      {failed ? (
        <p className="text-xs text-destructive">{t('miniapp.player.report_failed')}</p>
      ) : null}
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!reason || busy}
          pending={busy}
          pendingOverlay={t('miniapp.loading')}
          onClick={() => {
            if (reason) void send(itemId, reason, note).then((ok) => ok && close());
          }}
        >
          {t('miniapp.player.report_send')}
        </Button>
        <Button size="sm" variant="outline" onClick={close}>
          {t('miniapp.player.report_cancel')}
        </Button>
      </div>
    </div>
  );
}
